import { useMemo, useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchBdPorFuera } from "@/lib/api";
import { ExternalLink, Search, AlertCircle, Loader2 } from "lucide-react";

/* ─────────── Helpers ─────────── */
const MES_ES = ["ene","feb","mar","abr","may","jun","jul","ago","sep","oct","nov","dic"];

function monthKeyNumeric(s) {
  if (!s) return [0, 0];
  const t = s.toLowerCase().replace(/[-.]/g, " ");
  const parts = t.split(/\s+/).filter(Boolean);
  let y = 0, m = 0;
  for (const p of parts) {
    if (/^\d{4}$/.test(p)) y = +p;
    else {
      const idx = MES_ES.indexOf(p.slice(0, 3));
      if (idx >= 0 && !m) m = idx + 1;
    }
  }
  return [y, m];
}

/* ─────────── Pills ─────────── */
function CanalPill({ canal }) {
  if (!canal) return <span className="text-[10px] text-slate-400">—</span>;
  const s = canal.toLowerCase();
  let tone = "bg-slate-100 text-slate-700 border-slate-200";
  if (/email|correo|mail/.test(s)) tone = "bg-rose-50 text-rose-700 border-rose-200";
  else if (/linkedin|li/.test(s)) tone = "bg-sky-50 text-sky-700 border-sky-200";
  else if (/whatsapp|wsp/.test(s)) tone = "bg-emerald-50 text-emerald-700 border-emerald-200";
  else if (/llamada|call|telef/.test(s)) tone = "bg-amber-50 text-amber-700 border-amber-200";
  return <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${tone} whitespace-nowrap`}>{canal}</span>;
}

function EstadoPill({ estado }) {
  if (!estado) return <span className="text-[10px] text-slate-400">—</span>;
  const s = estado.toLowerCase();
  let tone = "bg-slate-100 text-slate-700 border-slate-200";
  if (/reu\s*ok|realizada|reunion ok/.test(s)) tone = "bg-emerald-50 text-emerald-700 border-emerald-200";
  else if (/responde|pendiente de rta/.test(s)) tone = "bg-sky-50 text-sky-700 border-sky-200";
  else if (/solicitud pendiente/.test(s)) tone = "bg-violet-50 text-violet-700 border-violet-200";
  else if (/no avanza|rechaz|cancelad/.test(s)) tone = "bg-rose-50 text-rose-700 border-rose-200";
  else if (/seguir|seguimiento/.test(s)) tone = "bg-amber-50 text-amber-700 border-amber-200";
  return <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${tone} whitespace-nowrap`}>{estado}</span>;
}

function TipoLeadPill({ tipo }) {
  if (!tipo) return <span className="text-[10px] text-slate-400">—</span>;
  const s = tipo.toLowerCase();
  let tone = "bg-slate-100 text-slate-700 border-slate-200";
  if (/lead principal/.test(s)) tone = "bg-indigo-50 text-indigo-700 border-indigo-200";
  else if (/referido|referidor/.test(s)) tone = "bg-violet-50 text-violet-700 border-violet-200";
  return <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-sm border ${tone} whitespace-nowrap truncate inline-block max-w-full`} title={tipo}>{tipo}</span>;
}

/* ─────────── Top-of-table scrollbar that mirrors the table scroll ─────────── */
function useDualScroll(width) {
  const topRef = useRef(null);
  const bottomRef = useRef(null);
  useEffect(() => {
    const top = topRef.current;
    const bot = bottomRef.current;
    if (!top || !bot) return undefined;
    let lock = false;
    const onTop = () => { if (lock) return; lock = true; bot.scrollLeft = top.scrollLeft; lock = false; };
    const onBot = () => { if (lock) return; lock = true; top.scrollLeft = bot.scrollLeft; lock = false; };
    top.addEventListener("scroll", onTop);
    bot.addEventListener("scroll", onBot);
    return () => { top.removeEventListener("scroll", onTop); bot.removeEventListener("scroll", onBot); };
  }, [width]);
  return { topRef, bottomRef };
}

/* ─────────── Main section ─────────── */
export default function PorFueraSection({ clientId, hasPorFuera }) {
  const [search, setSearch] = useState("");
  const [mesFilter, setMesFilter] = useState("Todos");
  const [canalFilter, setCanalFilter] = useState("Todos");
  const [estadoFilter, setEstadoFilter] = useState("Todos");
  const [perfilFilter, setPerfilFilter] = useState("Todos");

  const { data, isLoading, error } = useQuery({
    queryKey: ["bd-por-fuera", clientId],
    queryFn: () => fetchBdPorFuera(clientId),
    enabled: !!clientId,
    staleTime: 60_000,
  });

  const leads = data?.leads || [];

  const meses = useMemo(() => {
    const set = Array.from(new Set(leads.map((l) => (l._mes || "").trim()).filter(Boolean)));
    return set.sort((a, b) => {
      const [ay, am] = monthKeyNumeric(a);
      const [by, bm] = monthKeyNumeric(b);
      return by - ay || bm - am;
    });
  }, [leads]);
  const canales = useMemo(() => Array.from(new Set(leads.map((l) => (l.canal || "").trim()).filter(Boolean))).sort(), [leads]);
  const estados = useMemo(() => Array.from(new Set(leads.map((l) => (l.estado || "").trim()).filter(Boolean))).sort(), [leads]);
  const perfiles = useMemo(() => Array.from(new Set(leads.map((l) => (l.perfil || "").trim()).filter(Boolean))).sort(), [leads]);

  const filtered = useMemo(() => leads.filter((l) => {
    if (mesFilter !== "Todos" && (l._mes || "").trim() !== mesFilter) return false;
    if (canalFilter !== "Todos" && (l.canal || "").trim() !== canalFilter) return false;
    if (estadoFilter !== "Todos" && (l.estado || "").trim() !== estadoFilter) return false;
    if (perfilFilter !== "Todos" && (l.perfil || "").trim() !== perfilFilter) return false;
    if (!search) return true;
    const blob = `${l.nombre} ${l.empresa} ${l.posicion} ${l.referido_de} ${l.canal} ${l.tipo_lead} ${l.estado} ${l.comentarios} ${l.accion_1} ${l.accion_2}`.toLowerCase();
    return blob.includes(search.toLowerCase());
  }), [leads, search, mesFilter, canalFilter, estadoFilter, perfilFilter]);

  const { topRef, bottomRef } = useDualScroll(filtered.length);

  if (!hasPorFuera) {
    return (
      <section className="bg-amber-50 border border-amber-200 rounded-md p-4 text-xs text-amber-900 flex items-start gap-3" data-testid="por-fuera-no-config">
        <AlertCircle className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
        <div>
          <div className="font-semibold mb-1">Sin Sheet de Por Fuera configurado.</div>
          <p>Pegá el link del Sheet de outreach manual en <span className="font-semibold">Configurar compromiso → Sheet Por Fuera</span> para empezar.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-white border border-slate-200 rounded-md p-5 min-w-0 max-w-full overflow-hidden" data-testid="por-fuera-section">
      <div className="flex items-end justify-between gap-3 flex-wrap mb-4">
        <div>
          <h3 className="text-base font-semibold text-slate-900">Por Fuera</h3>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Outreach manual (referidos, contactos directos) sincronizado del Sheet.
            <span className="ml-1 font-mono text-slate-700">{filtered.length}</span> de
            <span className="ml-1 font-mono text-slate-700">{leads.length}</span> leads.
          </p>
        </div>
        {data?.sheet_url && (
          <a
            href={data.sheet_url.replace("/export?format=csv", "/edit").replace(/&?gid=(\d+)/, "#gid=$1")}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-900"
            data-testid="por-fuera-sheet-link"
          >
            <ExternalLink className="h-3 w-3" /> Abrir Sheet
          </a>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre, empresa, comentario…"
            className="w-full h-9 pl-8 pr-3 text-xs border border-slate-200 rounded-md bg-white"
            data-testid="por-fuera-search"
          />
        </div>
        {perfiles.length > 1 && (
          <select value={perfilFilter} onChange={(e) => setPerfilFilter(e.target.value)} className="h-9 text-xs border border-slate-200 rounded-md px-2 bg-white" data-testid="por-fuera-perfil">
            <option value="Todos">Todos los perfiles</option>
            {perfiles.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        )}
        <select value={canalFilter} onChange={(e) => setCanalFilter(e.target.value)} className="h-9 text-xs border border-slate-200 rounded-md px-2 bg-white" data-testid="por-fuera-canal">
          <option value="Todos">Todos los canales</option>
          {canales.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={estadoFilter} onChange={(e) => setEstadoFilter(e.target.value)} className="h-9 text-xs border border-slate-200 rounded-md px-2 bg-white" data-testid="por-fuera-estado">
          <option value="Todos">Todos los estados</option>
          {estados.map((e) => <option key={e} value={e}>{e}</option>)}
        </select>
        <select value={mesFilter} onChange={(e) => setMesFilter(e.target.value)} className="h-9 text-xs border border-slate-200 rounded-md px-2 bg-white" data-testid="por-fuera-mes">
          <option value="Todos">Todos los meses</option>
          {meses.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>

      {isLoading && (
        <div className="py-12 flex items-center justify-center text-xs text-slate-500" data-testid="por-fuera-loading">
          <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Cargando leads…
        </div>
      )}
      {error && (
        <div className="bg-rose-50 border border-rose-200 rounded-md p-3 text-xs text-rose-700">
          Error al cargar el Sheet: {error?.response?.data?.detail || error.message}
        </div>
      )}
      {!isLoading && !error && (
        <>
          <div ref={topRef} className="overflow-x-scroll -mx-5 mb-1 bd-leads-scrollbar h-[12px]" data-testid="por-fuera-scroll-top">
            <div className="min-w-[1900px] h-[1px]" aria-hidden="true" />
          </div>
          <div ref={bottomRef} className="overflow-x-auto -mx-5 border-t border-slate-100">
            <table className="w-full text-[11px] min-w-[1900px] table-fixed">
              <colgroup>
                <col className="w-[100px]" />
                <col className="w-[110px]" />
                <col className="w-[140px]" />
                <col className="w-[170px]" />
                <col className="w-[160px]" />
                <col className="w-[140px]" />
                <col className="w-[200px]" />
                <col className="w-[140px]" />
                <col className="w-[160px]" />
                <col className="w-[160px]" />
                <col className="w-[130px]" />
                <col className="w-[200px]" />
              </colgroup>
              <thead className="bg-slate-50 border-y border-slate-200 sticky top-0">
                <tr className="text-left text-slate-500 uppercase tracking-wider text-[10px] whitespace-nowrap">
                  <th className="px-2 py-2 font-semibold">Fecha últ. acción</th>
                  <th className="px-2 py-2 font-semibold">Canal</th>
                  <th className="px-2 py-2 font-semibold">Tipo de lead</th>
                  <th className="px-2 py-2 font-semibold">Nombre del referido</th>
                  <th className="px-2 py-2 font-semibold">Empresa</th>
                  <th className="px-2 py-2 font-semibold">Posición</th>
                  <th className="px-2 py-2 font-semibold">Correo / Info</th>
                  <th className="px-2 py-2 font-semibold">Referido de</th>
                  <th className="px-2 py-2 font-semibold">Acción 1</th>
                  <th className="px-2 py-2 font-semibold">Acción 2</th>
                  <th className="px-2 py-2 font-semibold">Estado</th>
                  <th className="px-2 py-2 font-semibold">Comentario extra</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.slice(0, 400).map((l, i) => {
                  const correo = l.correo || l.info || "";
                  const isEmail = /@/.test(correo);
                  return (
                  <tr key={i} className="hover:bg-slate-50" data-testid={`por-fuera-row-${i}`}>
                    <td className="px-2 py-1.5">
                      <div className="font-mono text-[10.5px] text-slate-700 tabular-nums">{l.fecha_ult_accion || "—"}</div>
                      {l._mes && <div className="font-mono text-[9.5px] text-slate-400 uppercase tracking-wider">{l._mes}</div>}
                    </td>
                    <td className="px-2 py-1.5"><CanalPill canal={l.canal} /></td>
                    <td className="px-2 py-1.5"><TipoLeadPill tipo={l.tipo_lead} /></td>
                    <td className="px-2 py-1.5 font-medium text-slate-800 truncate" title={l.nombre}>{l.nombre || "—"}</td>
                    <td className="px-2 py-1.5 text-slate-700 truncate" title={l.empresa}>{l.empresa || "—"}</td>
                    <td className="px-2 py-1.5 text-slate-600 truncate" title={l.posicion}>{l.posicion || "—"}</td>
                    <td className="px-2 py-1.5 text-slate-600 truncate" title={correo}>
                      {correo
                        ? (isEmail
                            ? <a href={`mailto:${correo}`} className="text-indigo-600 hover:underline break-all">{correo}</a>
                            : <span className="break-all">{correo}</span>)
                        : "—"}
                    </td>
                    <td className="px-2 py-1.5 text-slate-600 truncate" title={l.referido_de}>{l.referido_de || "—"}</td>
                    <td className="px-2 py-1.5 text-slate-600 truncate" title={l.accion_1}>{l.accion_1 || "—"}</td>
                    <td className="px-2 py-1.5 text-slate-600 truncate" title={l.accion_2}>{l.accion_2 || "—"}</td>
                    <td className="px-2 py-1.5"><EstadoPill estado={l.estado} /></td>
                    <td className="px-2 py-1.5 text-slate-600 truncate" title={l.comentarios}>{l.comentarios || "—"}</td>
                  </tr>
                );})}
                {filtered.length > 400 && (
                  <tr><td colSpan={12} className="text-center text-[10px] text-slate-400 italic py-3">+ {filtered.length - 400} leads más. Filtrá para acotar.</td></tr>
                )}
                {filtered.length === 0 && (
                  <tr><td colSpan={12} className="text-center text-[11px] text-slate-400 italic py-6">Sin leads que coincidan con los filtros.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
